import { isRouteErrorResponse, useRouteError, Link } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { Home, RefreshCw, AlertTriangle, WifiOff, ShieldX, Search, ServerCrash } from 'lucide-react'

const ERROR_CONFIG: Record<number, { icon: any; title: string; message: string; color: string }> = {
  400: { icon: AlertTriangle, title: 'Requête invalide', message: 'La requête envoyée est incorrecte. Vérifiez les données saisies.', color: 'bg-amber-50 text-amber-500' },
  403: { icon: ShieldX, title: 'Accès refusé', message: 'Vous n\'avez pas les droits pour accéder à cette page. Connectez-vous ou contactez-nous.', color: 'bg-red-50 text-red-500' },
  404: { icon: Search, title: 'Page introuvable', message: 'La page que vous cherchez n\'existe pas ou a été déplacée.', color: 'bg-blue-50 text-blue-500' },
  500: { icon: ServerCrash, title: 'Erreur serveur', message: 'Une erreur technique est survenue. Notre équipe est informée et travaille à la résoudre.', color: 'bg-red-50 text-red-500' },
  502: { icon: WifiOff, title: 'Service indisponible', message: 'Le serveur ne répond pas momentanément. Réessayez dans quelques instants.', color: 'bg-amber-50 text-amber-500' },
  503: { icon: WifiOff, title: 'Maintenance en cours', message: 'Nous effectuons une maintenance. Le service sera rétabli très prochainement.', color: 'bg-blue-50 text-blue-500' },
}

export function GlobalErrorBoundary() {
  const error = useRouteError()
  const isRouteError = isRouteErrorResponse(error)

  const status = isRouteError ? error.status : 500
  const config = ERROR_CONFIG[status] ?? ERROR_CONFIG[500]
  const Icon = config.icon

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />

      <div className="mx-auto max-w-lg px-4 py-20 text-center">
        <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-2xl ${config.color}`}>
          <Icon className="h-8 w-8" />
        </div>

        <p className="mt-4 text-5xl font-extrabold text-slate-200">{status}</p>
        <h1 className="mt-2 text-2xl font-bold text-slate-900">{config.title}</h1>
        <p className="mt-2 text-sm text-slate-500">{config.message}</p>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover btn-press"
          >
            <Home className="h-4 w-4" /> Retour à l'accueil
          </Link>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-border px-6 py-3 text-sm font-semibold text-slate-700 hover:bg-white btn-press"
          >
            <RefreshCw className="h-4 w-4" /> Réessayer
          </button>
        </div>

        {/* Quick links */}
        <div className="mt-8 flex flex-wrap justify-center gap-2">
          {[
            { href: '/services', label: 'Services' },
            { href: '/guide-prix', label: 'Guide prix' },
            { href: '/demande-devis/1', label: 'Devis gratuit' },
            { href: '/contact', label: 'Contact' },
          ].map((link) => (
            <Link key={link.href} to={link.href}
              className="rounded-full border border-border px-3 py-1 text-xs text-slate-500 hover:border-primary hover:text-primary">
              {link.label}
            </Link>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  )
}
